// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Course-mapping GPS capture. ANDROID PHONE ONLY — every entry point below no-ops on
// web and iOS (see isSupported). Web cannot hold a foreground service and tab suspension
// would kill collection; iOS needs its own consent copy, Info.plist strings and App Store
// conversation, and is deferred.
//
// Runs as a foreground service (visible notification) rather than using
// ACCESS_BACKGROUND_LOCATION: that gives continuous location for a whole round while
// avoiding the Play Store background-location review, and it is the honest categorisation
// since the app IS the active round tracker.

import * as Location from 'expo-location';
import { Platform } from 'react-native';

import type {
  CourseTraceBatch,
  CourseTraceFix,
  CourseTraceMarker,
} from '../features/course-trace/types';
import { enqueueTraceBatch, flushTraceBatches, type TraceUploader } from './courseTraceQueue';
import { createEventId } from './offlineQueue';

/** One fix per 3s. A putting dwell lasts minutes, so this resolves it comfortably while
 *  costing roughly a third of what 1 Hz would. */
const SAMPLE_INTERVAL_MS = 3000;

/** Let the GNSS hardware buffer fixes so the application processor wakes about once a
 *  minute instead of every 3s. The AP wakeups, not the receiver, dominate the power
 *  budget — this is the single biggest battery lever in the capture path. */
const BATCH_WAIT_MS = 60000;

/** A green is ~20m across, so a worse fix carries no signal about where it is. */
const MAX_ACCURACY_METERS = 15;

/** Flush thresholds. Whichever comes first. */
const MAX_FIXES_PER_BATCH = 500;
const MAX_BATCH_AGE_MS = 5 * 60 * 1000;

/** Never start below this, and stop if the level falls to it mid-round. A dead phone on
 *  the 16th is a far worse outcome than a missing trace. */
export const MIN_BATTERY_FRACTION = 0.2;

export const isSupported = (): boolean => Platform.OS === 'android';

type Session = {
  roundId: string;
  courseId: string;
  deviceModel: string | null;
  holeNumber: number | null;
  fixes: CourseTraceFix[];
  markers: CourseTraceMarker[];
  openedAt: number;
  subscription: Location.LocationSubscription | null;
};

let session: Session | null = null;

export type StartTraceOptions = {
  roundId: string;
  courseId: string;
  deviceModel?: string | null;
  /** Whether the player has opted in. Capture never starts without this. */
  hasConsent: boolean;
  /** Whether the course still needs traces. A fully mapped course is not re-surveyed. */
  mappingStatus: 'needed' | 'complete';
  /** Battery level 0..1, or null when unknown. */
  batteryFraction: number | null;
};

export type StartTraceResult =
  | 'started'
  | 'unsupported-platform'
  | 'no-consent'
  | 'course-complete'
  | 'battery-too-low'
  | 'permission-denied'
  | 'already-running';

/**
 * Starts capturing a trace for a round. Safe to call repeatedly; a second call while a
 * session is open is a no-op.
 */
export const startCourseTrace = async (options: StartTraceOptions): Promise<StartTraceResult> => {
  if (!isSupported()) return 'unsupported-platform';
  if (!options.hasConsent) return 'no-consent';

  // Collection retires itself: once a course is mapped there is nothing left to learn
  // there, so the battery cost stops for everyone who plays it.
  if (options.mappingStatus === 'complete') return 'course-complete';

  if (options.batteryFraction !== null && options.batteryFraction < MIN_BATTERY_FRACTION) {
    return 'battery-too-low';
  }
  if (session) return 'already-running';

  const { status } = await Location.requestForegroundPermissionsAsync();
  if (status !== 'granted') return 'permission-denied';

  const active: Session = {
    roundId: options.roundId,
    courseId: options.courseId,
    deviceModel: options.deviceModel ?? null,
    holeNumber: null,
    fixes: [],
    markers: [],
    openedAt: Date.now(),
    subscription: null,
  };
  session = active;

  active.subscription = await Location.watchPositionAsync(
    {
      accuracy: Location.Accuracy.High,
      timeInterval: SAMPLE_INTERVAL_MS,
      distanceInterval: 0,
      // Hardware batching. Older expo-location builds ignore unknown options rather
      // than throwing, so this degrades to per-fix wakeups instead of failing.
      ...({ mayShowUserSettingsDialog: false, timeIntervalMaxWait: BATCH_WAIT_MS } as object),
    },
    (position) => {
      void onFix(position);
    }
  );

  return 'started';
};

const onFix = async (position: Location.LocationObject): Promise<void> => {
  const active = session;
  if (!active) return;

  const accuracy = position.coords.accuracy;
  // Drop at capture time: an inaccurate fix costs storage and battery to ship and then
  // dilutes the cluster it lands in.
  if (accuracy === null || accuracy === undefined || accuracy <= 0 || accuracy > MAX_ACCURACY_METERS) {
    return;
  }

  active.fixes.push({
    holeNumber: active.holeNumber,
    lat: position.coords.latitude,
    lon: position.coords.longitude,
    accuracy,
    altitude: position.coords.altitude ?? null,
    speed: position.coords.speed ?? null,
    clientTs: new Date(position.timestamp).toISOString(),
  });

  if (shouldSeal(active)) {
    await sealBatch(active);
  }
};

const shouldSeal = (active: Session): boolean =>
  active.fixes.length >= MAX_FIXES_PER_BATCH || Date.now() - active.openedAt >= MAX_BATCH_AGE_MS;

const sealBatch = async (active: Session): Promise<void> => {
  if (active.fixes.length === 0 && active.markers.length === 0) {
    active.openedAt = Date.now();
    return;
  }

  const batch: CourseTraceBatch = {
    batchId: createEventId(),
    courseId: active.courseId,
    roundId: active.roundId,
    deviceModel: active.deviceModel,
    points: active.fixes,
    markers: active.markers,
  };

  active.fixes = [];
  active.markers = [];
  active.openedAt = Date.now();

  await enqueueTraceBatch(batch);
};

/** Tells the capture which hole the player is on. Fixes carry it so the server can bound
 *  attribution without trusting marker positions. */
export const setCourseTraceHole = (holeNumber: number | null): void => {
  if (session) session.holeNumber = holeNumber;
};

/** Records a behaviour marker. Attribution only — see CourseTraceMarker. */
export const recordCourseTraceMarker = (
  kind: CourseTraceMarker['kind'],
  holeNumber: number | null
): void => {
  if (!session) return;
  session.markers.push({ kind, holeNumber, clientTs: new Date().toISOString() });
};

/** Called when the battery level changes. Stops capture rather than risking the round. */
export const onBatteryLevelChanged = async (fraction: number | null): Promise<void> => {
  if (fraction !== null && fraction < MIN_BATTERY_FRACTION) {
    await stopCourseTrace();
  }
};

/** Stops capture and seals whatever is buffered. Safe to call when not running. */
export const stopCourseTrace = async (): Promise<void> => {
  const active = session;
  if (!active) return;
  session = null;

  active.subscription?.remove();
  active.subscription = null;
  await sealBatch(active);
};

export const isCourseTraceRunning = (): boolean => session !== null;

/** Uploads whatever is buffered. Call on reconnect and after a round finishes. */
export const flushCourseTraces = async (upload: TraceUploader): Promise<number> => {
  if (!isSupported()) return 0;
  return flushTraceBatches(upload);
};

export const __resetCourseTraceForTests = (): void => {
  session?.subscription?.remove();
  session = null;
};
